// `resourceRef` → Atlas-route (a mélylink). A `resourceRef` LOGIKAI hivatkozás (`atlas:{kind}:{id}`), NEM
// útvonal — így egy route-átnevezés nem érvényteleníti a MÁR kiküldött értesítéseket: a képezés ITT él.
//
// A minta a globális kereső cél-feloldása (`components/shared/search-targets.ts`): a `refToRoute` egy TIPIZÁLT
// leírót ad (unit-tesztelhető, nincs router-függés), a `navigateToNotificationRoute` pedig LITERÁL
// `navigate`-hívásokkal navigál — így a TanStack a `to`-literálból helyesen szűkít (nincs `as`, nincs `any`).
//
// NEM kattintható (a `refToRoute` `null`-t ad):
//   • `resourceRef === null` — pl. TÖRÖLT meeting (a rekord megszűnt; a snapshot-adat a `data`-ban van),
//   • ISMERETLEN `kind` (jövőbeli szerver-verzió).
import type { useNavigate } from '@tanstack/react-router';

/** A resource-ref `kind`-jai (a szerver `resourceRefFor()`-jának tükre). */
export const NOTIFICATION_REF_KINDS = [
  'ticket',
  'message',
  'meeting',
  'asset',
  'client',
  'service-assignment',
  // TÖBBES SZÁM = a LISTA, nem egy sor. A hamarosan-induló ÖSSZESÍTŐ küldi (`atlas:service-assignments:…`):
  // ott nincs EGYETLEN hozzárendelés, amit ki lehetne emelni — a teendő a lista végigjárása.
  'service-assignments',
] as const;

export type NotificationRefKind = (typeof NOTIFICATION_REF_KINDS)[number];

/** A REGISZTRÁLT route-fa tipizált navigate-fn-je (a search-targets mintája). */
type TypedNavigate = ReturnType<typeof useNavigate>;

/**
 * A mélylink leírója — a route-fa VALÓS útvonalai. A detail-oldalak `$id`-paramot kapnak, a sheet-alapú
 * lista-oldalak a repo `?open=` deep-link konvencióját (lásd `routes/messages.tsx` · `meetings.tsx` · `assets.tsx`).
 */
export type NotificationRoute =
  | { to: '/tickets/$id'; params: { id: string } }
  | { to: '/clients/$id'; params: { id: string } }
  | { to: '/messages'; search: { open: string } }
  | { to: '/meetings'; search: { open: string } }
  | { to: '/assets'; search: { open: string; scope?: 'request' } }
  // A hozzárendelés-lista NEM sheet-alapú deep-linket kap (nincs `?open=` szerkesztő) — a cél a SOR
  // KIEMELÉSE (`asg_highlight`), ugyanaz a param, amit a kihagyás-napló sor-akciója is használ. A lista a
  // `/services` lap első füle, ezért a fül is megy vele (a prefix azért kell, mert a lapon két lista ül).
  // A kiemelés ELHAGYHATÓ: az ÖSSZESÍTŐ értesítés magára a listára visz (ott áll a figyelem-sáv is).
  | { to: '/services'; search: { tab: 'assignments'; asg_highlight?: string } };

/** Az igény-folyamat értesítés-típusai — ezek a Kérések fül sávját nyitják (a lejárat-figyelmeztetés nem). */
const ASSET_REQUEST_TYPES: ReadonlySet<string> = new Set([
  'asset.requested',
  'asset.approved',
  'asset.approved_info',
  'asset.rejected',
  'asset.purchased',
  'asset.request_updated',
]);

/** `atlas:{kind}:{id}` → `{ kind, id }`; érvénytelen/idegen prefix → `null`. */
export function parseResourceRef(ref: string | null): { kind: string; id: string } | null {
  if (!ref) return null;
  // Pontosan 3 szegmens (`atlas` · kind · id) — az id-ben nincs `:` (UUID).
  const parts = ref.split(':');
  if (parts.length !== 3) return null;
  const [app, kind, id] = parts;
  if (app !== 'atlas' || !kind || !id) return null;
  return { kind, id };
}

/**
 * A mélylink cél-route-ja, vagy `null` ha a sor NEM kattintható (nincs ref / ismeretlen kind / nincs route).
 * A `null` NEM hiba: a panel ilyenkor statikus sorként rendereli (nem visz 404-re).
 */
export function refToRoute(ref: string | null, type?: string): NotificationRoute | null {
  const parsed = parseResourceRef(ref);
  if (!parsed) return null;
  const { kind, id } = parsed;
  switch (kind) {
    case 'ticket':
      return { to: '/tickets/$id', params: { id } };
    case 'client':
      // A bankkártya-lejárat az ÜGYFÉL-detailre visz (a kártyák az ügyfélhez tartoznak).
      return { to: '/clients/$id', params: { id } };
    case 'message':
      return { to: '/messages', search: { open: id } };
    case 'meeting':
      return { to: '/meetings', search: { open: id } };
    case 'asset':
      // az igény-értesítések a Kérések fülre visznek (a levél mélylinkjével azonos); a lejárat a leltárra
      if (type !== undefined && ASSET_REQUEST_TYPES.has(type)) {
        return { to: '/assets', search: { scope: 'request', open: id } };
      }
      return { to: '/assets', search: { open: id } };
    case 'service-assignment':
      return { to: '/services', search: { tab: 'assignments', asg_highlight: id } };
    case 'service-assignments':
      // ÖSSZESÍTŐ: a LISTÁRA visz, kiemelés nélkül. Az `id` szegmens a NÉZET neve (a ref-alak három szegmens
      // marad), ezért nem is használjuk fel — egy sor kiemelése itt félrevezető lenne.
      return { to: '/services', search: { tab: 'assignments' } };
    default:
      return null;
  }
}

/** Van-e mélylink (kattintható-e a sor)? */
export function isNavigableRef(ref: string | null, type?: string): boolean {
  return refToRoute(ref, type) !== null;
}

/** A leíró → tényleges navigáció. Minden ág LITERÁL `navigate`-hívás (típus-szűkítés cast nélkül). */
export function navigateToNotificationRoute(
  navigate: TypedNavigate,
  route: NotificationRoute,
): void {
  switch (route.to) {
    case '/tickets/$id':
      void navigate({ to: '/tickets/$id', params: route.params });
      return;
    case '/clients/$id':
      void navigate({ to: '/clients/$id', params: route.params });
      return;
    case '/messages':
      void navigate({ to: '/messages', search: route.search });
      return;
    case '/meetings':
      void navigate({ to: '/meetings', search: route.search });
      return;
    case '/assets':
      void navigate({ to: '/assets', search: route.search });
      return;
    case '/services':
      void navigate({ to: '/services', search: route.search });
      return;
    default: {
      // Exhaustiveness-guard: új NotificationRoute-ág itt FORDÍTÁSI hibát ad (nem néma no-op).
      const exhaustive: never = route;
      void exhaustive;
    }
  }
}
